/**
 * The mount of the basin a vanity will actually be built with.
 *
 * There are two different questions here and they were sharing one name.
 *
 *   1. What kind of sink do we go LOOKING for? — `styleConfig.sink
 *      .fallbackSinkType`. It shapes the catalog query and the fit rule
 *      (a vessel may overhang the front, so its depth is unconstrained in
 *      the search). This is an intent, held before we know what we'll find.
 *
 *   2. What is the basin we END UP with? — this module. If a usable catalog
 *      model was chosen, its own mount; otherwise the bowl is one we DRAW,
 *      and that is an undermount by construction (`proceduralBasin`).
 *
 * Reading (1) as the answer to (2) is a bug that has now been fixed three
 * times in different places, because the search coming back empty does not
 * make the drawn bowl a vessel:
 *
 *   - `versions.js` 2.235.0 — the standalone-sink swap built a VESSEL from
 *     the style fallback; fixed for that caller by passing the mount in.
 *   - `assembleVanity` — the same fall-through for every other caller, so
 *     a generated vanity's undermount basin was seated on the counter.
 *   - `vanity.js` — kept its own `sinkType` from the style fallback and fed
 *     it to `calculateSinkDepthOffset`, `faucetBackSpace` and
 *     `getAllowedFaucetMountTypes`. A vessel is pushed FORWARD on the deck
 *     because it may overhang the front lip; applying that to an undermount
 *     drove the bowl 3.4" out, past the front of both the cabinet and the
 *     countertop, on a 12.3"-deep vanity.
 *
 * One definition, so the mount that sizes the object and the mount that
 * seats it cannot disagree.
 */

const { getSinkType } = require('../sink-types');
const { PROCEDURAL_BASIN_SINK_TYPE } = require('./proceduralBasin');
const { INCHES_TO_METERS, VANITY_DIMENSIONS } = require('./constants');

/** How far a basin's top must stand above the unit before it reads as a
 *  bowl SITTING on the deck rather than one set into it. */
const VESSEL_MIN_PROUD_M = 2 * INCHES_TO_METERS;

/**
 * Does this model give us a footprint we can build from?
 *
 * The catalog stores INCHES. A value that isn't a usable number is treated
 * exactly like no model at all — the same rule the height uses — so an
 * untrusted catalog row can never put NaN into a stored extent.
 *
 * @param {Object|null} sinkModelData
 * @returns {boolean}
 */
function hasUsableCatalogFootprint(sinkModelData) {
  const width = Number(sinkModelData?.dimensions?.width) * INCHES_TO_METERS;
  const depth = Number(sinkModelData?.dimensions?.depth) * INCHES_TO_METERS;
  return (
    Number.isFinite(width) && width > 0 && Number.isFinite(depth) && depth > 0
  );
}

/**
 * @param {Object} options
 * @param {string} [options.inputSinkType] - an explicit mount from the caller,
 *   which always wins (`swapToCountertopMountedSink` passes one).
 * @param {Object|null} [options.sinkModelData] - the chosen catalog model.
 * @param {string} [options.styleFallbackSinkType] - the style's preference,
 *   consulted ONLY for a model that has a footprint but names no mount.
 * @returns {string} canonical sinkType
 */
function resolveSinkMount({
  inputSinkType,
  sinkModelData,
  styleFallbackSinkType,
} = {}) {
  if (inputSinkType) return inputSinkType;
  // No usable model: the bowl is the one we draw.
  if (!hasUsableCatalogFootprint(sinkModelData)) {
    return PROCEDURAL_BASIN_SINK_TYPE;
  }
  return (
    sinkModelData?.sinkType ||
    getSinkType(sinkModelData) ||
    (sinkModelData?.['vessel-bottom'] ? 'vessel' : null) ||
    styleFallbackSinkType
  );
}

/**
 * Which mount a SCANNED basin is, read off the boxes RoomPlan gave us.
 *
 * This is the question asked BEFORE the catalog search — it decides which
 * shelf we go looking on, and `resolveSinkMount` above then says what we
 * ended up with. Getting it wrong is not a near miss: search the vessel
 * shelf and a vessel is what you find, and it is then seated ON the counter.
 *
 * The old rule asked only whether the basin's TOP stood proud of the unit,
 * which a basin set INTO a counter routinely does — RoomPlan's box for one
 * spans the bowl AND the tap area above it. 89823100's is 11.8" tall against
 * a 32.7" cabinet: its top clears the unit by 7.2", so the rule called it a
 * vessel, went to the vessel shelf, and fitted a Kohler Conical Bell round
 * vessel bowl standing on the deck of an ordinary vanity.
 *
 * The discriminator it was missing is the BOTTOM. A vessel rests on the
 * deck; a basin whose box reaches below the deck is passing through it, and
 * that is an undermount whatever its top does — 89823100's reaches 4.7"
 * below. "Below" is measured against one countertop thickness, so the slab
 * the basin is set into cannot itself read as passing through.
 *
 * @param {Object} o
 * @param {number} o.sinkTopY    - top of the scanned basin's box
 * @param {number} o.sinkBottomY - bottom of it
 * @param {number} o.cabinetTopY - top of the scanned vanity unit
 * @returns {'vessel'|'undermount'}
 */
function scannedSinkMount({ sinkTopY, sinkBottomY, cabinetTopY }) {
  if (!Number.isFinite(sinkTopY) || !Number.isFinite(cabinetTopY)) {
    return PROCEDURAL_BASIN_SINK_TYPE;
  }
  const standsProud = sinkTopY - cabinetTopY >= VESSEL_MIN_PROUD_M;
  if (!standsProud) return 'undermount';
  // An unusable bottom leaves only the old evidence; a basin standing proud
  // with nothing to contradict it is the vessel this rule always called it.
  if (!Number.isFinite(sinkBottomY)) return 'vessel';
  const reachesBelowDeck =
    cabinetTopY - sinkBottomY > VANITY_DIMENSIONS.countertop.thickness;
  return reachesBelowDeck ? 'undermount' : 'vessel';
}

module.exports = {
  resolveSinkMount,
  hasUsableCatalogFootprint,
  scannedSinkMount,
  VESSEL_MIN_PROUD_M,
};
